import { formatBytes, matchesFileType } from '../../shared-ui-layer/types/validation.types'

import type { ValidationError } from '../../shared-ui-layer/types/validation.types'
import type { FileUploadValidation } from '../types'

export type { FileUploadValidation } from '../types'

/**
 * Validate document files against size and type rules.
 *
 * NOTE: This function only validates file size and type.
 * maxFiles validation is handled at the composable level (usePostEditor, CommentEditor, etc.)
 * to allow selecting multiple files and then enforcing the limit when adding to the model.
 *
 * @param files - Array of File objects to validate
 * @param validation - Validation rules (FileUploadValidation)
 * @returns Object with valid files and validation errors
 *
 * @example
 * const { valid, errors } = await validateDocumentFiles(
 *   fileList,
 *   { maxFileSize: 10 * 1024 * 1024, acceptedTypes: ['application/pdf', 'text/plain'] }
 * )
 * // valid: File[] — files that passed all checks
 * // errors: ValidationError[] — details for each failed file
 */
export const validateDocumentFiles = async (
  files: File[],
  validation: FileUploadValidation
): Promise<{ valid: File[]; errors: ValidationError[] }> => {
  const errors: ValidationError[] = []
  const validFiles: File[] = []

  // Validate each file
  for (const file of files) {
    const fileErrors: ValidationError[] = []

    // File size validation
    if (validation.maxFileSize && file.size > validation.maxFileSize) {
      fileErrors.push({
        field: 'fileSize',
        message: `File "${file.name}" exceeds ${formatBytes(validation.maxFileSize)}`,
        type: 'error',
        meta: { fileName: file.name, size: file.size, maxSize: validation.maxFileSize },
      })
    }

    if (validation.minFileSize && file.size < validation.minFileSize) {
      fileErrors.push({
        field: 'fileSize',
        message: `File "${file.name}" is smaller than ${formatBytes(validation.minFileSize)}`,
        type: 'error',
        meta: { fileName: file.name, size: file.size, minSize: validation.minFileSize },
      })
    }

    // MIME type validation
    if (validation.acceptedTypes && validation.acceptedTypes.length > 0) {
      const isAccepted = validation.acceptedTypes.some(acceptedType => matchesFileType(file.type, acceptedType))
      if (!isAccepted) {
        fileErrors.push({
          field: 'fileType',
          message: `File type "${file.type}" not accepted for "${file.name}"`,
          type: 'error',
          meta: { fileName: file.name, fileType: file.type, acceptedTypes: validation.acceptedTypes },
        })
      }
    }

    if (fileErrors.length === 0) {
      validFiles.push(file)
    } else {
      errors.push(...fileErrors)
    }
  }

  return { valid: validFiles, errors }
}
